class WCAGFormValidator extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.errorSummary = null;
    this.errorList = null;
    this._errorIdCounter = 0;
    this.requiredMessage = 'This field is required';
    this.emailMessage = 'Enter a valid email address, such as name@example.com';
  }

  connectedCallback() {
    const selector = this.dataset.form;
    this.requiredMessage = this.dataset.requiredMessage || 'This field is required';
    this.emailMessage = this.dataset.emailMessage || 'Enter a valid email address, such as name@example.com';
    this.form = selector ? document.querySelector(selector) : (this.querySelector('form') || this.closest('form'));
    if (!this.form) return;

    // Find summary/list within the form
    this.errorSummary = this.form.querySelector('[data-error-summary]') || null;
    this.errorList = this.form.querySelector('[data-error-list]') || null;

    // Attach listeners for clearing/showing inline errors
    this._attachFieldListeners(this.form);
    // Observe dynamic additions (e.g., rows added later)
    this._observeMutations();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }

  _observeMutations() {
    this._observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches('input, select')) {
            this._attachFieldListenersTo(n);
          } else {
            n.querySelectorAll && this._attachFieldListeners(n);
          }
        });
      }
    });
    this._observer.observe(this.form, { childList: true, subtree: true });
  }

  _attachFieldListeners(root) {
    root.querySelectorAll('input, select').forEach((el) => this._attachFieldListenersTo(el));
  }

  _attachFieldListenersTo(el) {
    const eventName = el.tagName.toLowerCase() === 'select' || el.type === 'file' ? 'change' : 'input';
    el.addEventListener(eventName, () => {
      if (this.isFieldValid(el)) this.clearFieldError(el);
    });
    el.addEventListener('blur', () => {
      if (!this.isFieldValid(el)) {
        const { label, message } = this.getFieldError(el);
        this.setFieldError(el, message || `${this.requiredMessage}.`);
      }
    });
  }

  validate() {
    const errors = [];
    const fields = Array.from(this.form.querySelectorAll('input, select, textarea'))
      .filter((el) => el.hasAttribute('required') || el.type === 'email' || el.name === 'item_qty');
    fields.forEach((input) => {
      const fieldError = this.getFieldError(input);
      if (fieldError) {
        const { message } = fieldError;
        errors.push(this.setFieldError(input, message));
      }
    });
    return errors;
  }

  showErrors(errors) {
    if (!this.errorSummary || !this.errorList) return;
    this.errorList.innerHTML = '';
    errors.forEach(({ label, message, field }) => {
      const id = field.id || field.name;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = id ? `#${id}` : '#';
      a.textContent = `${label}: ${message}`;
      a.addEventListener('click', (e) => {
        e.preventDefault();
        field.focus();
      });
      li.appendChild(a);
      this.errorList.appendChild(li);
    });
    this.errorSummary.hidden = false;
    this.errorSummary.focus({ preventScroll: true });
    this.errorSummary.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  clear() {
    if (this.errorSummary) {
      this.errorSummary.hidden = true;
      if (this.errorList) this.errorList.innerHTML = '';
    }
    this.form?.querySelectorAll('[data-error-for]')?.forEach((el) => {
      el.hidden = true;
      el.textContent = '';
    });
    this.form?.querySelectorAll('input, select, textarea')?.forEach((el) => {
      el.removeAttribute('aria-invalid');
      const describedBy = (el.getAttribute('aria-describedby') || '')
        .split(' ')
        .filter((id) => id && !id.endsWith('-error'))
        .join(' ');
      if (describedBy) el.setAttribute('aria-describedby', describedBy);
      else el.removeAttribute('aria-describedby');
      this.removeErrorBorder(el);
    });
  }

  // Inline helpers
  clearFieldError(input) {
    const container = input.closest('.flex, .flex-col') || input.parentElement;
    const key = input.id || input.name;
    const errorEl = container?.querySelector(`[data-error-for="${key}"]`) || this.form.querySelector(`#error-${key}`);
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    input.removeAttribute('aria-invalid');
    const ids = (input.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter((id) => id && id !== errorEl?.id)
      .join(' ');
    if (ids) input.setAttribute('aria-describedby', ids);
    else input.removeAttribute('aria-describedby');
    this.removeErrorBorder(input);
  }

  isFieldValid(input) {
    if (input.type === 'file') {
      return input.files && input.files.length > 0;
    }
    if (input.tagName.toLowerCase() === 'select') {
      return Boolean(input.value);
    }
    if (input.name === 'item_qty' || input.id === 'item_qty') {
      const n = Number(input.value);
      return Number.isFinite(n) && n >= 1;
    }
    if (input.type === 'email') {
      const v = (input.value || '').trim();
      if (!v) return false;
      return /.+@.+\..+/.test(v);
    }
    if (input.type === 'checkbox') {
      return !input.hasAttribute('required') || input.checked;
    }
    return !input.hasAttribute('required') || Boolean((input.value || '').trim());
  }

  getFieldLabel(input) {
    if (input.id) {
      const label = this.form.querySelector(`label[for="${input.id}"]`);
      if (label) {
        return label.textContent.replace(/\s*\*+\s*$/, '').trim();
      }
    }
    const name = input.name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    return name || 'This field';
  }

  ensureErrorId(errorEl, input) {
    if (!errorEl.id) {
      const base = (input.id || input.name || 'field').replace(/\s+/g, '-');
      this._errorIdCounter += 1;
      errorEl.id = `${base}-error-${this._errorIdCounter}`;
    }
    return errorEl.id;
  }

  setFieldError(input, message) {
    const container = input.closest('.flex, .flex-col') || input.parentElement;
    const key = input.id || input.name;
    const errorEl = container?.querySelector(`[data-error-for="${key}"]`) || this.form.querySelector(`#error-${key}`);
    const label = this.getFieldLabel(input);
    const text = message || `${this.requiredMessage}.`;
    if (errorEl) {
      errorEl.textContent = text;
      errorEl.hidden = false;
      const errorId = this.ensureErrorId(errorEl, input);
      const describedBy = (input.getAttribute('aria-describedby') || '').trim();
      const parts = describedBy ? describedBy.split(' ') : [];
      if (!parts.includes(errorId)) parts.push(errorId);
      input.setAttribute('aria-describedby', parts.join(' ').trim());
    }
    input.setAttribute('aria-invalid', 'true');
    this.addErrorBorder(input);
    return { label, message: text, field: input };
  }

  getFieldError(input) {
    const label = this.getFieldLabel(input);
    if (input.type === 'email') {
      const v = (input.value || '').trim();
      if (!v) return { label, message: this.requiredMessage };
      if (!/.+@.+\..+/.test(v)) return { label, message: this.emailMessage };
      return null;
    }
    if (input.name === 'item_qty') {
      const n = Number(input.value);
      if (!Number.isFinite(n) || n < 1) return { label, message: this.requiredMessage };
      return null;
    }
    if (input.type === 'file') {
      if (!input.files || input.files.length === 0) return { label, message: this.requiredMessage };
      return null;
    }
    if (input.tagName.toLowerCase() === 'select') {
      if (!input.value) return { label, message: this.requiredMessage };
      return null;
    }
    if (input.type === 'checkbox') {
      if (!input.checked) return { label, message: this.requiredMessage };
      return null;
    }
    if (input.hasAttribute('required') && !Boolean((input.value || '').trim())) return { label, message: this.requiredMessage };
    return null;
  }

  addErrorBorder(input) {
    if (input.type === 'file') {
      const proxy = input.nextElementSibling;
      if (proxy && proxy.nodeName === 'DIV') {
        proxy.classList.add('border', 'border-red-43');
      }
      return;
    }
    input.classList.add('border', 'border-red-43');
  }

  removeErrorBorder(input) {
    if (input.type === 'file') {
      const proxy = input.nextElementSibling;
      if (proxy && proxy.nodeName === 'DIV') {
        proxy.classList.remove('border', 'border-red-43');
      }
      return;
    }
    input.classList.remove('border', 'border-red-43');
  }
}

customElements.define('wcag-form-validator', WCAGFormValidator);


